import { DisplayItem } from 'design-system';
import { uniqueId } from 'lodash';

export type AdditionalBrokerForm = {
  id: string;
  value: string;
  validationError: boolean;
  error: boolean;
  errorText: string;
};

// Constants
export const ADD_INPUT = 'ADD_INPUT';
export const REMOVE_INPUT = 'REMOVE_INPUT';
export const CHANGE_INPUT = 'CHANGE_INPUT';
export const CLEAR_ALL = 'CLEAR_ALL';
export const ERROR_HANDLER = 'ERROR_HANDLER';
export const VALIDATION_HANDLER = 'VALIDATION_HANDLER';

export const emptyErrorMessage = `Por favor seleccione una licencia`;
export const duplicateErrorMessage = `Licencia duplicada, por favor seleccione nuevamente`;

type HandlerInputTypes = 'ADD_INPUT' | 'REMOVE_INPUT';
type ErrorClearInputTypes =
  | 'CLEAR_ALL'
  | 'ERROR_HANDLER'
  | 'VALIDATION_HANDLER';
type ChangeInputTypes = 'CHANGE_INPUT';

export type ChangeInput = { type: ChangeInputTypes; id: string; value: string };
export type HandlerInput = {
  type: HandlerInputTypes;
  id: string;
};
export type ErrorClearInput = {
  type: ErrorClearInputTypes;
};

export type ReducerAction = HandlerInput | ChangeInput | ErrorClearInput;

export const cobrokerageList = [
  'PJ756',
  'PJ956',
  'PJ416',
  'PJ424',
  'PJ182',
  'PJ367',
  'PJ313',
  'PJ676',
];

// Validates if a selected license matches the one above.
export const matchesCobrokerageLicense = (currentLicense: string): boolean => {
  return (
    cobrokerageList.find((brokerage) => currentLicense === brokerage) !==
    undefined
  );
};

// Empty input when a user adds an additional license
export const emptyInput: AdditionalBrokerForm = {
  id: '',
  value: '',
  // This validates everytime an input changes
  validationError: true,
  // This validates when the user presses the input button (form's error input)
  error: false,
  errorText: '',
};

// Validates if the form validation contains an error.
export const additionalBrokerHasError = (
  inputs: AdditionalBrokerForm[],
): boolean => {
  if (inputs.length > 0) {
    return inputs.find((broker) => broker.validationError) !== undefined;
  }
  return false;
};

// Check if an input value is empty
const checkEmptyValue = (
  broker: AdditionalBrokerForm,
  handler: ErrorClearInputTypes,
): AdditionalBrokerForm => {
  if (broker.value === '') {
    if (handler === ERROR_HANDLER) {
      return {
        ...broker,
        error: true,
        errorText: emptyErrorMessage,
      };
    }
    return {
      ...broker,
      validationError: true,
    };
  }
  return broker;
};

// Changes the input value
const changeInputValue = (
  broker: AdditionalBrokerForm,
  action: ChangeInput,
) => {
  if (broker.id === action.id) {
    return {
      ...broker,
      value: action.value,
      validationError: false,
      error: false,
      errorText: '',
    };
  }
  return broker;
};

// Converts the values to match the form
export const convertToAdditionalBrokerForm = (
  currentBrokers: string[],
): AdditionalBrokerForm[] => {
  return currentBrokers.map((broker) => {
    return {
      ...emptyInput,
      validationError: false,
      id: uniqueId(),
      value: broker,
    };
  });
};

// Dynamic dropdown where selected values are removed in the next dropdown
export const filterDropdownValues = (
  brokersList: DisplayItem[],
  brokersValue: AdditionalBrokerForm[],
  broker: AdditionalBrokerForm,
): DisplayItem[] => {
  const selectedOthers = brokersValue
    .filter((oneBroker) => oneBroker.value !== broker.value)
    .map((single) => single.value);
  return brokersList.filter(
    (singleBroker) => !selectedOthers.includes(singleBroker.Value),
  );
};

/**
 * This reducer will control all additional broker license dropdown inputs
 * including, change, remove, add, error handlers and validations.
 * * There are 2 Error handlers, one for the form when the user submits,
 * and one for every input change, it is done like this because we don't want
 * to show an error as soon as the value is empty (first add)
 * */
export const brokerReducer = (
  state: AdditionalBrokerForm[],
  action: ReducerAction,
): AdditionalBrokerForm[] => {
  switch (action.type) {
    case CHANGE_INPUT:
      return state.map((broker) => changeInputValue(broker, action));
    case ADD_INPUT:
      return [...state, { ...emptyInput, id: action.id }];
    case REMOVE_INPUT:
      return state.filter((broker) => broker.id !== action.id);
    case ERROR_HANDLER:
      return state.map((broker) => checkEmptyValue(broker, ERROR_HANDLER));
    case VALIDATION_HANDLER:
      return state.map((broker) => checkEmptyValue(broker, VALIDATION_HANDLER));
    default:
      return [];
  }
};
